import { Truck, MapPin, Phone, User, CheckSquare, Package } from 'lucide-react';
import type { PosSettings, ReceiptData, ReceiptTemplateSettings } from '@/types';
import { resolveFileUrl } from '@/utils/fileUrl';
import { companyContactParts, SellerIdsBlock } from './blocks';

export interface DeliverySlipProps {
  inv: ReceiptData;
  displayCompany: string;
  servedBy?: string;
  tmplSettings: ReceiptTemplateSettings;
  posSettings?: PosSettings | null;
}

export function DeliverySlipLayout({
  inv,
  displayCompany,
  servedBy,
  tmplSettings,
  posSettings,
}: DeliverySlipProps) {
  const contact = companyContactParts(inv, tmplSettings);

  const totalQuantity = (inv.items || []).reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0
  );

  const recipientName =
    (inv.delivery_recipient_name || inv.customer_name || 'Walk In Customer').trim();
  const recipientPhone =
    (inv.delivery_phone || (inv as any).customer_mobile || (inv as any).mobile || '').trim();
  const deliveryAddress =
    (inv.delivery_address || (inv as any).customer_address || '').trim();
  const postalCode = (inv.delivery_postal_code || '').trim();
  const deliveryNotes = (inv.delivery_notes || '').trim();
  const deliveryDriver = (inv.delivery_driver || '').trim();
  const deliveryStatus = (inv.delivery_status || 'Pending').trim();

  const orderDate = new Date(inv.creation || inv.invoice_date || Date.now());
  const dateStr = orderDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const timeStr = orderDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div
      className="invoice layout-delivery-slip bg-white text-slate-800 rounded-lg overflow-hidden"
      style={{
        maxWidth: 580,
        margin: '0 auto',
        boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
        border: '1px solid #e2e8f0',
      }}
    >
      {/* Top Banner Header */}
      <div className="bg-slate-900 text-white p-5 border-b-4 border-amber-500">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            {tmplSettings.show_logo && inv.company_logo && (
              <img
                src={resolveFileUrl(inv.company_logo)}
                alt={displayCompany}
                className="mb-2 max-h-12 max-w-[140px] object-contain rounded bg-white/10 p-1"
              />
            )}
            <h2 className="text-xl font-bold tracking-tight uppercase">{displayCompany}</h2>
            <SellerIdsBlock posSettings={posSettings} align="left" className="text-[11px] text-slate-300 mt-0.5 space-y-0.5" />
            {contact.length > 0 && (
              <p className="text-xs text-slate-300 mt-1">{contact.join(' · ')}</p>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500 text-slate-950 font-bold text-xs uppercase tracking-wider">
              <Truck className="w-3.5 h-3.5" />
              <span>Delivery Slip</span>
            </div>
            <div className="text-xs font-mono text-slate-300 mt-2">
              Order #{inv.name}
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              {dateStr} {timeStr}
            </div>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Status and Driver Bar */}
        <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200/80 text-xs">
          <div>
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">
              Delivery Status
            </span>
            <span className="inline-flex items-center gap-1 font-bold text-slate-800 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
              {deliveryStatus}
            </span>
          </div>
          <div>
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">
              Assigned Driver
            </span>
            <span className="font-bold text-slate-800 mt-0.5 flex items-center gap-1">
              <User className="w-3.5 h-3.5 text-slate-500" />
              {deliveryDriver || 'Unassigned'}
            </span>
          </div>
        </div>

        {/* Destination & Recipient Card */}
        <div className="bg-amber-50/70 border border-amber-300/80 rounded-xl p-4 text-xs space-y-2">
          <div className="flex items-center gap-1.5 font-bold text-amber-900 text-sm border-b border-amber-200/80 pb-1.5">
            <MapPin className="w-4 h-4 text-amber-700" />
            <span>Delivery Destination</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-700 pt-1">
            <div>
              <span className="font-semibold text-slate-900 block text-[11px]">Recipient Name:</span>
              <span className="text-sm font-bold text-slate-900">{recipientName}</span>
            </div>
            <div>
              <span className="font-semibold text-slate-900 block text-[11px]">Contact Phone:</span>
              <span className="text-sm font-semibold text-slate-900 flex items-center gap-1">
                <Phone className="w-3 h-3 text-slate-500" />
                {recipientPhone || '—'}
              </span>
            </div>
          </div>

          <div className="pt-1">
            <span className="font-semibold text-slate-900 block text-[11px]">Delivery Address:</span>
            <p className="text-xs text-slate-800 font-medium whitespace-pre-wrap bg-white/70 p-2 rounded-lg border border-amber-200/60 mt-0.5">
              {deliveryAddress || 'No address specified'}
              {postalCode ? ` (Postal Code: ${postalCode})` : ''}
            </p>
          </div>

          {deliveryNotes && (
            <div className="pt-1">
              <span className="font-semibold text-amber-900 block text-[11px]">
                Special Instructions / Notes:
              </span>
              <p className="text-xs text-amber-950 font-medium italic bg-amber-100/70 p-2 rounded-lg border border-amber-300/60 mt-0.5">
                {deliveryNotes}
              </p>
            </div>
          )}
        </div>

        {/* Packing Checklist & Items Table (NO PRICES) */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 uppercase tracking-wider">
              <Package className="w-4 h-4 text-slate-600" />
              <span>Items To Deliver (Packing Checklist)</span>
            </div>
            <span className="text-[11px] font-semibold text-slate-500">
              No Prices Included
            </span>
          </div>

          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3 w-10 text-center font-bold">Check</th>
                  <th className="py-2.5 px-3 font-bold">Item Description</th>
                  <th className="py-2.5 px-3 w-20 text-right font-bold">Qty</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(inv.items || []).map((item: any, idx: number) => {
                  const itemTitle = item.item_name || item.item_code || 'Item';
                  const variant = item.variant_name ? ` (${item.variant_name})` : '';
                  const code = tmplSettings.show_item_code && item.item_code ? `[${item.item_code}] ` : '';

                  return (
                    <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-2.5 px-3 text-center align-middle">
                        <span className="inline-block w-4 h-4 rounded border-2 border-slate-400 bg-white" />
                      </td>
                      <td className="py-2.5 px-3 align-middle">
                        <div className="font-bold text-slate-900">
                          {code}{itemTitle}{variant}
                        </div>
                        {item.line_comment ? (
                          <div className="text-xs text-slate-500 italic mt-0.5 font-normal">{item.line_comment}</div>
                        ) : null}
                      </td>
                      <td className="py-2.5 px-3 text-right align-middle font-mono font-bold text-sm text-slate-900">
                        {item.quantity}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Physical Package Summary (ZERO PRICES) */}
        <div className="bg-slate-100/90 rounded-xl p-3 border border-slate-200 flex items-center justify-between text-xs font-semibold text-slate-800">
          <div className="flex items-center gap-2">
            <CheckSquare className="w-4 h-4 text-emerald-600" />
            <span>Total Package Count:</span>
          </div>
          <div className="space-x-3 font-bold text-sm">
            <span>{inv.items?.length || 0} unique items</span>
            <span className="text-slate-400">|</span>
            <span className="text-slate-900">{totalQuantity} total qty</span>
          </div>
        </div>

        {/* Recipient Acknowledgment & Signature */}
        <div className="border border-dashed border-slate-300 rounded-xl p-3.5 space-y-3 bg-slate-50/50 text-[11px] text-slate-600">
          <p className="font-semibold text-slate-700 italic">
            Please inspect and verify package contents upon delivery.
          </p>
          <div className="grid grid-cols-2 gap-4 pt-1">
            <div>
              <span className="text-[10px] text-slate-500 uppercase block mb-1">
                Dispatched By / Driver
              </span>
              <div className="h-8 border-b border-slate-400 flex items-end pb-1 font-medium text-slate-800">
                {deliveryDriver || servedBy || ''}
              </div>
            </div>
            <div>
              <span className="text-[10px] text-slate-500 uppercase block mb-1">
                Received By (Customer Signature)
              </span>
              <div className="h-8 border-b border-slate-400 flex items-end pb-1 text-slate-400 italic">
                Signature
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-[10px] text-slate-400 pt-2 border-t border-slate-100">
          <p>Thank you for choosing {displayCompany}! For delivery inquiries, contact our store.</p>
        </div>
      </div>
    </div>
  );
}
